import { useRouterState } from "@tanstack/react-router";
import { clsx } from "clsx";
import { ChevronDown } from "lucide-react";
import { useSidebarStore } from "@/hooks/useSidebar";
import type { NavGroup } from "@/types/navigation";
import { isPathActive, SidebarNavItem } from "./SidebarNavItem";

interface SidebarNavGroupProps {
  group: NavGroup;
}

export function SidebarNavGroup({ group }: SidebarNavGroupProps) {
  const expandedGroups = useSidebarStore((s) => s.expandedGroups);
  const toggleGroup = useSidebarStore((s) => s.toggleGroup);
  const isExpanded = expandedGroups.has(group.id);

  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  const hasActiveChild = group.children.some((child) =>
    isPathActive(pathname, child.href, child.exact),
  );

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          toggleGroup(group.id);
        }}
        className={clsx(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium",
          "cursor-pointer transition-colors duration-150",
          hasActiveChild
            ? "text-white"
            : "text-adara-200 hover:bg-white/5 hover:text-white",
        )}
        aria-expanded={isExpanded}
      >
        <span
          className={clsx(
            "flex h-5 w-5 shrink-0 items-center justify-center",
            hasActiveChild ? "text-gold-400" : "text-adara-300",
          )}
        >
          {group.icon}
        </span>
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown
          size={14}
          className={clsx(
            "shrink-0 text-adara-400 transition-transform duration-200",
            isExpanded && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      <div
        className={clsx(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <ul className="mt-0.5 flex flex-col gap-0.5 pb-1 pl-5">
            {group.children.map((child) => (
              <SidebarNavItem key={child.id} item={child} nested />
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}
